import { useAdminFloorPlans } from 'restaurantAdmin/hooks/useAdminFloorPlans';
import { FloorPlan } from '@components/floorPlan/FloorPlan';
import { FloorPlanSelector } from '@components/floorPlan/FloorPlanSelector';
import { interactiveFloorPlanTablesRendererFactory } from '@components/floorPlan/floorPlanTablesRendererFactory';
import { Icon } from '@components/icon/Icon';
import { type FloorPlanTableData } from '@shared/types/floorPlans';
import cx from 'classnames';
import { difference, union } from 'lodash-es';
import { UseFormReturn } from 'react-hook-form';
import typography from '~styles/typography.scss';
import styles from './AddOrRemoveTables.scss';
import { ShiftFormData } from './ShiftForm';

interface AddOrRemoveTablesProps {
  form: UseFormReturn<ShiftFormData>;
}

export const AddOrRemoveTables = ({ form }: AddOrRemoveTablesProps) => {
  const { setValue, watch } = form;
  const { floorPlans, selectedFloorPlan, setSelectedFloorPlanId } =
    useAdminFloorPlans();

  const formFloorPlanTableIds = watch('floorPlanTableIds') || [];

  const handleFloorPlanTableOnClick = (floorPlanTable: FloorPlanTableData) => {
    // Add the table to highlightedTableIds if it's not already there
    if (!formFloorPlanTableIds.includes(floorPlanTable.id)) {
      setValue('floorPlanTableIds', [
        ...formFloorPlanTableIds,
        floorPlanTable.id,
      ]);
    } else {
      // Otherwise, remove it
      setValue(
        'floorPlanTableIds',
        formFloorPlanTableIds.filter((id) => id !== floorPlanTable.id),
      );
    }
  };

  const calculateIsHighlighted = (floorPlanTable: FloorPlanTableData) =>
    formFloorPlanTableIds.some(
      (highlightedTableId: string) => highlightedTableId === floorPlanTable.id,
    );

  const selectAll = () => {
    setValue(
      'floorPlanTableIds',
      union(
        formFloorPlanTableIds,
        selectedFloorPlan.floorPlanTables.map((t) => t.id),
      ),
    );
  };

  const clearSelection = () => {
    const selectedFloorPlanTableIds = selectedFloorPlan.floorPlanTables.map(
      (t) => t.id,
    );

    // Selected tables can only be cleared for the currently selected floor
    // plan, so we remove ALL the tables for the currently selected floor plan
    // from the form state.
    setValue(
      'floorPlanTableIds',
      difference(formFloorPlanTableIds, selectedFloorPlanTableIds),
    );
  };

  if (!selectedFloorPlan) return;

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <div>
          <span className={cx(typography.t1, styles.instructions)}>
            Select tables to add to shift
          </span>
          <button
            className={styles.selectAll}
            onClick={selectAll}
            type="button"
          >
            <span>Select All of These Tables</span>
          </button>
        </div>
        <button
          className={styles.clearSelection}
          onClick={clearSelection}
          type="button"
        >
          <Icon name="rotateCcw" ariaLabel="clear selection" />
          Clear selection
        </button>
      </div>
      <div className={styles.floorPlan}>
        <div className={styles.floorPlanSelect}>
          <FloorPlanSelector
            floorPlans={floorPlans}
            selectedFloorPlan={selectedFloorPlan}
            onChange={(selectedFloorPlanId) => {
              const newFloorPlan = floorPlans.find(
                (fp) => fp.id === selectedFloorPlanId,
              );
              if (newFloorPlan) {
                setSelectedFloorPlanId(newFloorPlan.id);
              }
            }}
          />
        </div>

        <FloorPlan
          backgroundSrc={selectedFloorPlan.backgroundSrc}
          floorPlanTablesRenderer={interactiveFloorPlanTablesRendererFactory({
            tables: selectedFloorPlan.floorPlanTables,
            calculateIsHighlighted,
            calculateIsSelectable: () => true,
            handleTableOnClick: handleFloorPlanTableOnClick,
          })}
        />
      </div>
    </div>
  );
};
